import * as yup from 'yup'
import Misc from 'src/utils/helper/misc'
import { findIndex, isEqual, sortBy } from 'lodash'

export default {
  setValidationSchema(self) {
    const data = self.submission
    const binaryData = self.$store.getters['binaries/list']
    const yupPackageSchema = yup.object({
      isValidBinary: yup.boolean().test('validateBinary', null, () => {
        const isBinaryValid = binaryData.some(binary => {
          return (
            binary.hasOwnProperty('validationResult') &&
            binary.validationResult.progress.complete !== true
          );
        });
      
        if (isBinaryValid) {
          return this.createError({
            message: self.$t('pages.submissions.review.approve.error_msg.validate_binary')
          });
        }
      
        return true; // Always return true for valid cases
      }),
      isErrorLevel: yup.boolean().test('validateOnErrorLevels', null, () => {
        if (self.validateErrorLevel(binaryData, data)) {
          return this.createError({
            message: self.$t('pages.submissions.review.approve.error_msg.validate_error_levels')
          });
        }
        return true;
      }),
      isUniqueName: yup.boolean().test('Unique', null, isUniqueName => {
        if (!isUniqueName) {
          return this.createError({
            message: self.$t('pages.submissions.review.approve.error_msg.is_unique_name')
          });
        }
        return true;
      }),
      userMetaData: yup.object({
        binaries: yup
          .array()
          .uniqueStyle(data.userMetaData.binaries)
          .uniqueSubpack(data.userMetaData.packages)
          .binaryRequired(data.userMetaData.binaries),
        category: yup.lazy(value => {
          if (value.length === 0) {
            return yup
              .array()
              .required(self.$t('pages.submissions.review.approve.error_msg.required', { field: 'category' }))
          }
          return yup.object().notRequired()
        }),
        familyDescriptions: yup.array(
          yup.object({
            description: yup.string().test('custom_required', null, description => {
              if (Misc.sanitizeHTML(description) || description.length === 0) {
                return new yup.ValidationError(
                  self.$t('pages.submissions.review.approve.error_msg.required', { field: 'description' })
                )
              }
            })
          })
        ),
        imageSortorders: yup.array(
          yup.object({
            imageIds: yup.array().test('PostersLengthForLive', null, imagesIds => {
              if (this.submission.isLive) {
                return true;
              }
              if (imagesIds.length < 5) {
                return this.createError({
                  message: self.$t('pages.submissions.review.approve.error_msg.posters')
                });
              }
              return true;
            })
          })
        ),
        designers: yup
          .array()
          .of(
            yup.object().shape({
              id: yup.string().required().label('id'),
              name: yup.string().required().label('name')
            })
          )
          .min(1, self.$t('pages.submissions.review.approve.error_msg.required', { field: 'designers' })),
        packages: yup
          .array(
            yup.object({
              name: yup
                .string()
                .required(self.$t('pages.submissions.review.approve.error_msg.required', { field: 'package' }))
            })
          )
          .pricesRequired(data.userMetaData.packages)
          .uniqueSubpack(data.userMetaData.packages)
          .validateSubpack(data.userMetaData.packages),
        tags: yup
          .array()
          .of(
            yup.object().shape({
              id: yup.string().required().label('id'),
              name: yup.string().required().label('name')
            })
          )
          .min(1, self.$t('pages.submissions.review.approve.error_msg.required', { field: 'tags' }))
      })
    })
    return yupPackageSchema
  },
  isPricesRequired(self) {
    yup.addMethod(yup.array, 'pricesRequired', function (propertyName, message) {
      return this.test('required', message, function () {
        let pricesError = []
        pricesError = propertyName
          .map(val => {
            if (val.prices.length === 0) {
              return self.$t('pages.submissions.review.approve.error_msg.required', { field: val.name + ' $ price' })
            }
            if (val.prices.length > 0) {
              const isUSD = val.prices.some(item => item.currency === 'USD')
              if (!isUSD) {
                return self.$t('pages.submissions.review.approve.error_msg.required', { field: val.name + ' $ price' })
              }
            }
          })
          .filter(val => val !== undefined)
        return this.createError({ path: this.path, message: pricesError })
      })
    })
  },
  checkSubpack(self) {
    yup.addMethod(yup.array, 'uniqueSubpack', function (propertyName, message) {
      return this.test('subPackageValidations', message, function () {
        // removed filter for subpackages, now it will check uniqueness across all package type names
        const subPackages = propertyName
        let errors = []
        if (subPackages.length > 0) {
          let subPackName = []
          let uniqueData = []
          let subPackErrors = []
          subPackName = subPackages.map(data => Misc.strToLowerCase(data.name))
          uniqueData = Misc.getDuplicates(subPackName)
          subPackErrors = uniqueData.map(val => findIndex(subPackName, item => item === val))
          errors = subPackErrors.map(val => {
            return self.$t('pages.submissions.review.approve.error_msg.unique_subpackage', {
              subPackName: subPackages[val].name
            })
          })
        }
        return this.createError({ path: this.path, message: errors })
      })
    })
    yup.addMethod(yup.array, 'validateSubpack', function (propertyName, message) {
      return this.test('subPackageValidations', message, function () {
        const subPackages = propertyName.filter(item => item.packageType === 'customPackage')
        const binaryCount = propertyName.filter(item => item.packageType === 'singleStyle').length
        let errors = []
        if (subPackages.length > 0) {
          errors = subPackages
            .map(val => {
              if (val.styles.length === 1) {
                if (binaryCount === 3) {
                  return self.$t('pages.submissions.review.approve.error_msg.atleast_2_subpackage', {
                    subPackName: val.name
                  })
                }
                return self.$t('pages.submissions.review.approve.error_msg.binary_in_subpackage', {
                  subPackName: val.name,
                  binaryCount: binaryCount - 1
                })
              }
              if (val.styles.length === binaryCount) {
                return self.$t('pages.submissions.review.approve.error_msg.binary_in_subpackage', {
                  subPackName: val.name,
                  binaryCount: binaryCount - 1
                })
              }
              if (val.styles.length === 0) {
                return self.$t('pages.submissions.review.approve.error_msg.required', {
                  field: 'select fonts (sub package ' + val.name + ')'
                })
              }
              if (val.styles.length > 1) {
                const selectDataCheck = subPackages
                  .map(value =>
                    isEqual(sortBy(val.styles, [o => o.binaryName]), sortBy(value.styles, [o => o.binaryName]))
                  )
                  .filter(item => item === true).length
                if (selectDataCheck > 1) {
                  return self.$t('pages.submissions.review.approve.error_msg.unique_content_subpackage')
                }
              }
            })
            .filter(val => val !== undefined)
        }
        return this.createError({ path: this.path, message: errors })
      })
    })
  },
  isStylesUnique(self) {
    yup.addMethod(yup.array, 'uniqueStyle', function (propertyName, message) {
      return this.test('uniqStyle', message, function () {
        let styleNameArray = []
        let uniqueResult = []
        let styleUrlErrors = []
        let styleUrlErrorsMsg = []
        styleNameArray = propertyName.map(val => Misc.spaceTodashString(Misc.strToLowerCase(val.styleName)))
        uniqueResult = Misc.getDuplicates(styleNameArray)
        styleUrlErrors = uniqueResult.map(val => findIndex(styleNameArray, item => item === val))
        styleUrlErrorsMsg = styleUrlErrors.map(val => {
          return self.$t('pages.submissions.review.approve.error_msg.unique_style', { index: val + 1 })
        })
        return this.createError({ path: this.path, message: styleUrlErrorsMsg })
      })
    })
  },
  isBinariesRequired(self) {
    yup.addMethod(yup.array, 'binaryRequired', function (propertyName, message) {
      return this.test('mandatory', message, function () {
        const errorMsg = Misc.binaryArrayError(propertyName, self)
        return this.createError({ path: this.path, message: errorMsg })
      })
    })
  },
  runAllValidations(self) {
    this.isPricesRequired(self)
    this.isStylesUnique(self)
    this.isBinariesRequired(self)
    this.checkSubpack(self)
  }
}
